import React, { useMemo, useState } from "react";
import { Tooltip, message } from "antd";
import LoanDocumentsModal from "./LoanDocumentsModal";
import ApproxClosureModal from "./ApproxClosureModal";
import Icon from "../../../../components/AppIcon";
import Button from "../../../../components/ui/Button";
import { Checkbox } from "../../../../components/ui/Checkbox";
import { formatINR } from "../../../../utils/currency";
import { calculateLivePrincipalOutstanding } from "../../../../utils/emiCalculator";
import { homeLoansApi } from "../../../../api/homeLoans";

const STAGES = [
  { key: "customerProfile", label: "Customer Profile" },
  { key: "prefileCompletion", label: "Pre-File Completion" },
  { key: "loginToBank", label: "Login to bank" },
  { key: "approval", label: "Approval" },
  { key: "disbursement", label: "Disbursement" },
  { key: "postfileCompletion", label: "Post-File Completion" },
  { key: "documentsAdded", label: "Documents added" },
  { key: "insurance", label: "Insurance" },
  { key: "invoice", label: "Invoice" },
  { key: "vehicleDelivery", label: "Home Delivery" },
  { key: "rc", label: "RC" },
];

const STAGE_INDEX_MAP = {
  profile: 0,
  customerprofile: 0,
  customer_profile: 0,
  prefile: 1,
  "pre-file": 1,
  login: 2,
  logintobank: 2,
  login_to_bank: 2,
  approval: 3,
  disbursement: 4,
  disbursal: 4,
  postfile: 5,
  "post-file": 5,
  documents: 6,
  docs: 6,
  insurance: 7,
  invoice: 8,
  delivery: 9,
  vehicledelivery: 9,
  rc: 10,
  payout: 10,
};

const toDateOrNull = (value) => {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    const ts = value > 1e12 ? value : value * 1000;
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === "object") {
    if (typeof value?.toDate === "function") {
      return toDateOrNull(value.toDate());
    }
    if (typeof value?.valueOf === "function") {
      const maybe = value.valueOf();
      if (maybe !== value) return toDateOrNull(maybe);
    }
    const nested = value?.$date ?? value?.date ?? value?.value ?? null;
    if (nested != null) return toDateOrNull(nested);
    return null;
  }

  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return null;

    const dmY = raw.match(
      /^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})(?:[,\s]+(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm)?)?$/i,
    );
    if (dmY) {
      let hour = Number(dmY[4] || 0);
      const minute = Number(dmY[5] || 0);
      const second = Number(dmY[6] || 0);
      const meridian = String(dmY[7] || "").toLowerCase();
      if (meridian === "pm" && hour < 12) hour += 12;
      if (meridian === "am" && hour === 12) hour = 0;

      const dd = dmY[1].padStart(2, "0");
      const mm = dmY[2].padStart(2, "0");
      const yyyy = dmY[3].length === 2 ? `20${dmY[3]}` : dmY[3];
      const hh = String(hour).padStart(2, "0");
      const min = String(minute).padStart(2, "0");
      const ss = String(second).padStart(2, "0");
      const normalized = `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
      const ts = Date.parse(normalized);
      if (Number.isFinite(ts)) return new Date(ts);
    }

    const ts = Date.parse(raw);
    if (Number.isFinite(ts)) return new Date(ts);
  }

  return null;
};

const earliestDate = (values = []) => {
  const valid = values.filter(Boolean);
  if (!valid.length) return null;
  return new Date(Math.min(...valid.map((d) => d.getTime())));
};

const hasDisplayValue = (value) => {
  if (value == null) return false;
  const text = String(value).trim();
  if (!text) return false;
  const normalized = text.toLowerCase();
  return ![
    "n/a",
    "na",
    "not set",
    "unknown",
    "-",
    "null",
    "undefined",
  ].includes(normalized);
};

const firstFilled = (...values) =>
  values.find(
    (v) =>
      v !== undefined &&
      v !== null &&
      !(typeof v === "string" && v.trim() === ""),
  );

const firstMeaningful = (...values) => values.find((v) => hasDisplayValue(v));
const AUTO_LOAN_NUMBER_REGEX = /^LN-\d{4}-\d+$/i;

const isAutoGeneratedLoanNumber = (value) =>
  AUTO_LOAN_NUMBER_REGEX.test(String(value || "").trim());

const normalizeLoanType = (loan) =>
  String(
    loan?.typeOfLoan || loan?.loanType || loan?.caseType || loan?.loan_type || "",
  )
    .trim()
    .toLowerCase();

const isNewCarLoan = (loan) => normalizeLoanType(loan) === "new car";

const getDisbursementDate = (loan) =>
  toDateOrNull(
    firstFilled(
      loan?.disbursement_date,
      loan?.approval_disbursedDate,
      loan?.disbursedDate,
      loan?.disbursementDate,
      loan?.approval_banksData?.[0]?.disbursedDate,
    ),
  );

const getDeliveryDate = (loan) =>
  toDateOrNull(
    firstFilled(
      loan?.delivery_date,
      loan?.deliveryDate,
      loan?.delivery_done_at,
      loan?.postfile_delivery_date,
      loan?.postfile_deliveryDate,
      loan?.postFile?.delivery_date,
      loan?.postFile?.deliveryDate,
      loan?.postfile?.delivery_date,
      loan?.postfile?.deliveryDate,
    ),
  );

const ageDaysFromDates = (baselineDate, completedDate = null) => {
  if (!baselineDate) return null;
  const end = completedDate || new Date();
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((end.getTime() - baselineDate.getTime()) / msPerDay);
};

const formatDateShort = (date) =>
  date
    ? date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "Not available";

const isCashCaseLoan = (loan) => {
  const typeText = normalizeLoanType(loan);
  if (typeText.includes("cash-in") || typeText.includes("cash in")) return false;
  if (
    typeText === "cash" ||
    typeText.includes("cash car") ||
    typeText.includes("cash sale")
  ) {
    return true;
  }
  const financedRaw = String(loan?.isFinanced ?? loan?.isFinanceRequired ?? "")
    .trim()
    .toLowerCase();
  if (["no", "false", "0"].includes(financedRaw)) return !typeText.includes("refinance");
  return loan?.isFinanced === false || loan?.isFinanceRequired === false;
};

const PREFILE_COMPLETION_THRESHOLD = 80;
const PREFILE_SECTION_MIN_FIELDS = 3;

const PREFILE_CO_APPLICANT_FIELD_GROUPS = [
  ["co_customerName", "co_name"],
  ["co_primaryMobile", "co_mobile"],
  ["co_dob"],
  ["co_pan"],
  ["co_aadhaar", "co_aadhar"],
  ["co_address"],
  ["co_pincode"],
  ["co_city"],
  ["co_occupation", "co_occupationType"],
];

const PREFILE_GUARANTOR_FIELD_GROUPS = [
  ["gu_customerName", "gu_name"],
  ["gu_primaryMobile", "gu_mobile"],
  ["gu_dob"],
  ["gu_pan"],
  ["gu_aadhaar", "gu_aadhar"],
  ["gu_address"],
  ["gu_pincode"],
  ["gu_city"],
  ["gu_occupation", "gu_occupationType"],
];

const getValueByPath = (obj, path) =>
  String(path || "")
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

const isTruthyYes = (value) => {
  if (value === true) return true;
  const text = String(value ?? "")
    .trim()
    .toLowerCase();
  return text === "yes" || text === "true" || text === "1";
};

const isMeaningfulPrefileValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return hasDisplayValue(value);
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "boolean") return true;
  if (Array.isArray(value)) return value.some((item) => isMeaningfulPrefileValue(item));
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value === "object") {
    if (typeof value?.isValid === "function") {
      try {
        return Boolean(value.isValid());
      } catch {
        return false;
      }
    }
    const nestedDate = toDateOrNull(value?.$date ?? value?.date ?? value?.value);
    if (nestedDate) return true;
    return Object.values(value).some((item) => isMeaningfulPrefileValue(item));
  }
  return false;
};

const buildPrefileSections = (loan, { isCashCase = false } = {}) => {
  const loanType = normalizeLoanType(loan);
  const isNewCar = loanType === "new car";
  const isUsedCar = loanType === "used car";
  const isCashIn = loanType.includes("cash-in") || loanType.includes("cash in");
  const isRefinance = loanType.includes("refinance");
  const sourceLower = String(firstFilled(loan?.recordSource, loan?.source, ""))
    .trim()
    .toLowerCase();
  const isIndirectSource = sourceLower.includes("indirect");
  const sections = [
    {
      key: "record",
      label: "Record",
      groups: [
        ["receivingDate"],
        ["recordSource", "source"],
        ["sourceName", "dealerName"],
        ["dealtBy"],
        ["docsPreparedBy"],
      ],
    },
    {
      key: "personal",
      label: "Personal",
      groups: [
        ["customerName", "applicant_name", "applicantName"],
        ["primaryMobile", "mobile", "phone", "phoneNumber"],
        ["dob"],
        ["gender"],
        ["maritalStatus"],
      ],
    },
    {
      key: "address",
      label: "Address",
      groups: [
        ["residenceAddress", "currentAddress", "address"],
        ["city"],
        ["pincode"],
        ["houseType"],
      ],
    },
    {
      key: "occupation",
      label: "Occupation",
      groups: [
        ["occupationType", "occupation", "professionalType", "companyType"],
        ["companyName"],
        ["designation"],
        ["experienceCurrent", "currentExperience", "totalExperience"],
        ["totalIncomeITR", "annualIncome", "monthlyIncome"],
      ],
    },
    {
      key: "banking",
      label: "Banking",
      groups: [
        ["bankName", "approval_bankName"],
        ["accountType"],
        ["accountNumber"],
        ["ifsc", "ifscCode"],
        ["branch"],
      ],
      include: () => !isCashCase,
    },
    {
      key: "vehicle",
      label: "Vehicle",
      groups: [
        ["typeOfLoan", "loanType"],
        ["usage"],
        ["vehicleMake"],
        ["vehicleModel"],
        ["vehicleVariant"],
        ["vehicleFuelType"],
      ],
    },
  ];

  if (isIndirectSource) {
    sections.push({
      key: "indirect",
      label: "Indirect Source",
      groups: [["dealerMobile"], ["dealerAddress"], ["payoutApplicable"]],
    });
    if (isTruthyYes(loan?.payoutApplicable)) {
      sections.push({
        key: "payout",
        label: "Payout",
        groups: [["prefile_sourcePayoutPercentage"]],
        minRequired: 1,
      });
    }
  }

  if (isNewCar) {
    if (!isCashCase) {
      sections.push({
        key: "dealer",
        label: "Dealer",
        groups: [
          ["showroomDealerName", "dealer_name_manual_prefile", "dealerName"],
          ["showroomDealerContactPerson", "dealerContactPerson"],
          ["showroomDealerContactNumber", "dealerContactNumber"],
          ["showroomDealerAddress", "dealerAddress"],
        ],
      });
    }
    const registrationGroups = [["registerSameAsAadhaar"]];
    const isSameAsAadhaarNo =
      String(loan?.registerSameAsAadhaar || "")
        .trim()
        .toLowerCase() === "no";
    if (isSameAsAadhaarNo) {
      registrationGroups.push(["registerSameAsPermanent"]);
      const isSameAsPermanentNo =
        String(loan?.registerSameAsPermanent || "")
          .trim()
          .toLowerCase() === "no";
      if (isSameAsPermanentNo) {
        registrationGroups.push(
          ["registrationAddress"],
          ["registrationPincode"],
          ["registrationCity", "postfile_regd_city"],
        );
      } else {
        registrationGroups.push(["registrationCity", "postfile_regd_city"]);
      }
    } else {
      registrationGroups.push(["registrationCity", "postfile_regd_city"]);
    }
    sections.push({
      key: "registration",
      label: "Registration",
      groups: registrationGroups,
      minRequired: Math.min(2, registrationGroups.length),
    });
  } else if (isUsedCar || isCashIn || isRefinance) {
    const nonNewCarGroups = [["valuation"], ["hypothecation"]];
    if (isTruthyYes(loan?.hypothecation)) {
      nonNewCarGroups.push(["hypothecationBank"]);
    }
    sections.push({
      key: "used-vehicle",
      label: "Used Vehicle",
      groups: nonNewCarGroups,
      minRequired: Math.min(2, nonNewCarGroups.length),
    });
  }

  if (isCashIn || isRefinance) {
    sections.push({
      key: "purpose",
      label: "Purpose",
      groups: [["purposeOfLoan"]],
      minRequired: 1,
    });
  }

  if (isTruthyYes(loan?.hasCoApplicant)) {
    sections.push({
      key: "coapplicant",
      label: "Co-Applicant",
      groups: PREFILE_CO_APPLICANT_FIELD_GROUPS,
    });
  }

  if (isTruthyYes(loan?.hasGuarantor)) {
    sections.push({
      key: "guarantor",
      label: "Guarantor",
      groups: PREFILE_GUARANTOR_FIELD_GROUPS,
    });
  }

  return sections.filter((section) =>
    typeof section.include === "function" ? section.include(loan) : true,
  );
};

const computePrefileCompletion = (loan, { isCashCase = false } = {}) => {
  if (isCashCase) {
    return {
      percentage: 100,
      completedSections: 1,
      totalSections: 1,
      pendingSections: [],
      sections: [
        {
          key: "cash-case",
          label: "Cash Case",
          filled: 1,
          total: 1,
          required: 1,
          isComplete: true,
        },
      ],
      isIncomplete: false,
    };
  }

  const sectionDefs = buildPrefileSections(loan, { isCashCase });
  const sectionResults = sectionDefs.map((section) => {
    const totalGroups = section.groups.length;
    const required = Math.min(
      section.minRequired ?? PREFILE_SECTION_MIN_FIELDS,
      totalGroups,
    );
    const filled = section.groups.reduce((count, alternatives) => {
      const isFilled = alternatives.some((path) =>
        isMeaningfulPrefileValue(getValueByPath(loan, path)),
      );
      return isFilled ? count + 1 : count;
    }, 0);
    return {
      key: section.key,
      label: section.label,
      filled,
      total: totalGroups,
      required,
      isComplete: filled >= required,
    };
  });

  const totalSections = sectionResults.length;
  const completedSections = sectionResults.filter((s) => s.isComplete).length;
  const percentage = totalSections
    ? Math.round((completedSections / totalSections) * 100)
    : 0;
  const pendingSections = sectionResults
    .filter((section) => !section.isComplete)
    .map((section) => section.label);

  return {
    percentage,
    completedSections,
    totalSections,
    pendingSections,
    sections: sectionResults,
    isIncomplete: percentage < PREFILE_COMPLETION_THRESHOLD,
  };
};

const getActualLoanNumber = (loan) => {
  const candidates = [loan?.loan_number, loan?.loanNumber];
  const match = candidates.find(
    (value) => hasDisplayValue(value) && !isAutoGeneratedLoanNumber(value),
  );
  return match ? String(match).trim() : "";
};

const getPendencyGateInfo = (loan) => {
  const isNewCar = isNewCarLoan(loan);
  const isCash = isCashCaseLoan(loan);
  const deliveryDate = getDeliveryDate(loan);
  const disbursementDate = getDisbursementDate(loan);

  if (isCash) {
    return {
      active: Boolean(deliveryDate),
      message: "Pendency starts after Delivery Date is captured for cash cases.",
    };
  }

  return {
    active: Boolean(disbursementDate),
    message: isNewCar
      ? "Pendency starts after Disbursement Date is captured for financed cases."
      : "Pendency starts after Disbursement Date is captured.",
  };
};

const buildPendencyRows = (loan) => {
  const isNewCar = isNewCarLoan(loan);
  const isCashCase = isCashCaseLoan(loan);
  const deliveryDate = getDeliveryDate(loan);
  const disbursementDate = getDisbursementDate(loan);
  const gateInfo = getPendencyGateInfo(loan);
  if (!gateInfo.active) return [];

  const invoiceDate = toDateOrNull(
    firstFilled(loan?.invoice_received_date, loan?.invoice_done_at, loan?.invoice_date),
  );
  const rcDate = toDateOrNull(firstFilled(loan?.rc_received_date, loan?.rc_received_at));

  const loanNoValue = getActualLoanNumber(loan);
  const loanNoDate = toDateOrNull(
    firstFilled(
      loan?.loan_number_date,
      loan?.loanNumberDate,
      loan?.loan_no_date,
      loan?.loan_assigned_date,
    ),
  );

  const regNoValue = firstMeaningful(
    loan?.rc_redg_no,
    loan?.vehicleRegNo,
    loan?.registrationNumber,
    loan?.vehicleNumber,
    "",
  );
  const regNoDate = toDateOrNull(
    firstFilled(
      loan?.rc_redg_date,
      loan?.registration_date,
      loan?.vehicle_registration_date,
    ),
  );

  const makeRow = ({
    key,
    label,
    baseline,
    completedDate,
    isReceived,
    actionType = null,
    editable = false,
  }) => ({
    key,
    label,
    baselineDate: baseline,
    completedDate,
    isReceived,
    actionType,
    editable,
    ageingDays: ageDaysFromDates(baseline, isReceived ? completedDate : null),
  });

  const rows = [];

  if (isNewCar) {
    rows.push(
      makeRow({
        key: "invoice",
        label: "Invoice",
        baseline: deliveryDate,
        completedDate: invoiceDate,
        isReceived: Boolean(invoiceDate),
        actionType: "delivery-invoice",
      }),
    );
  }

  rows.push(
    makeRow({
      key: "rc",
      label: "RC",
      baseline: isNewCar ? deliveryDate : disbursementDate,
      completedDate: rcDate,
      isReceived: Boolean(rcDate),
      actionType: "delivery-rc",
    }),
  );

  if (!isCashCase) {
    rows.push(
      makeRow({
        key: "loanNo",
        label: "Loan No.",
        baseline: disbursementDate,
        completedDate: loanNoDate,
        isReceived: Boolean(loanNoValue),
        editable: true,
      }),
    );
  }

  if (isNewCar) {
    rows.push(
      makeRow({
        key: "regNo",
        label: "Regd No.",
        baseline: deliveryDate,
        completedDate: regNoDate,
        isReceived: Boolean(regNoValue),
        editable: true,
      }),
    );
  }

  return rows;
};

const findStageIndexByCurrentStage = (loan) => {
  const rawStage = String(loan?.currentStage || "")
    .trim()
    .toLowerCase();
  if (!rawStage) return -1;
  const normalized = rawStage
    .replace(/\s+/g, "")
    .replace(/_/g, "")
    .replace(/-/g, "");
  if (Object.prototype.hasOwnProperty.call(STAGE_INDEX_MAP, rawStage)) {
    return STAGE_INDEX_MAP[rawStage];
  }
  if (Object.prototype.hasOwnProperty.call(STAGE_INDEX_MAP, normalized)) {
    return STAGE_INDEX_MAP[normalized];
  }
  if (normalized.includes("prefile")) return 1;
  if (normalized.includes("login")) return 2;
  if (normalized.includes("approv")) return 3;
  if (normalized.includes("disburs")) return 4;
  if (normalized.includes("postfile")) return 5;
  if (normalized.includes("doc")) return 6;
  if (normalized.includes("insur")) return 7;
  if (normalized.includes("invo")) return 8;
  if (normalized.includes("deliv")) return 9;
  if (normalized.includes("rc")) return 10;
  return -1;
};

const buildRawTimeline = (loan) => {
  const banks = Array.isArray(loan?.approval_banksData)
    ? loan.approval_banksData
    : [];
  const bankHistoryEvents = banks.flatMap((bank) =>
    Array.isArray(bank?.statusHistory) ? bank.statusHistory : [],
  );
  const approvalHistoryEvents = Array.isArray(loan?.approval_statusHistory)
    ? loan.approval_statusHistory
    : [];
  const allStatusEvents = bankHistoryEvents.concat(approvalHistoryEvents);

  const getEventDate = (event) =>
    toDateOrNull(
      event?.changedAt ||
        event?.date ||
        event?.createdAt ||
        event?.created_at ||
        event?.timestamp,
    );

  const getFirstEventByStatus = (token) => {
    const tokenLower = String(token || "").toLowerCase();
    const matchingDates = allStatusEvents
      .filter((event) =>
        String(event?.status || "")
          .toLowerCase()
          .includes(tokenLower),
      )
      .map(getEventDate)
      .filter(Boolean);
    return earliestDate(matchingDates);
  };

  const getLoginToBankDate = () => {
    const explicitAddedDates = banks
      .flatMap((bank) => [bank?.addedAt, bank?.createdAt, bank?.created_at])
      .map(toDateOrNull)
      .filter(Boolean);

    const bankAddedDates = bankHistoryEvents
      .filter((event) =>
        String(event?.note || event?.message || event?.remark || "")
          .toLowerCase()
          .includes("bank added"),
      )
      .map(getEventDate)
      .filter(Boolean);

    if (bankAddedDates.length) return earliestDate(bankAddedDates);

    const fallbackHistoryDates = bankHistoryEvents
      .map(getEventDate)
      .filter(Boolean);
    return earliestDate(fallbackHistoryDates.concat(explicitAddedDates));
  };

  const getDocumentsAddedDate = () => {
    const docPools = [
      loan?.postfile_documents,
      loan?.postfile_documents_ledger,
      loan?.postFile?.documents,
      loan?.postfile?.documents,
    ].filter(Array.isArray);

    const uploadedDates = docPools
      .flat()
      .map((doc) =>
        toDateOrNull(
          doc?.uploadedAt ||
            doc?.uploaded_at ||
            doc?.createdAt ||
            doc?.created_at ||
            doc?.date ||
            doc?.timestamp,
        ),
      )
      .filter(Boolean);

    return earliestDate(uploadedDates);
  };

  const customerProfileDate = toDateOrNull(
    firstFilled(
      loan?.profile_completed_at,
      loan?.profile_completion_date,
      loan?.receivingDate,
      loan?.createdAt,
    ),
  );
  const prefileCompletionDate = toDateOrNull(
    firstFilled(
      loan?.prefile_completed_at,
      loan?.prefile_completion_date,
      loan?.approval_approvalDate,
    ),
  );
  const loginToBankDate = getLoginToBankDate();
  const approvalDate =
    toDateOrNull(loan?.approval_approvalDate) ||
    getFirstEventByStatus("approv") ||
    toDateOrNull(loan?.postfile_approvalDate);
  const disbursementDate = getDisbursementDate(loan);
  const postfileCompletionDate = toDateOrNull(loan?.dispatch_date);
  const documentsAddedDate =
    getDocumentsAddedDate() ||
    toDateOrNull(loan?.documents_collected_at || loan?.docs_collected_at);
  const insuranceDate = toDateOrNull(
    firstFilled(
      loan?.insurance_done_at,
      loan?.insurance_policy_start_date,
      loan?.insurance_start_date,
    ),
  );
  const invoiceDate = toDateOrNull(
    firstFilled(loan?.invoice_received_date, loan?.invoice_done_at, loan?.invoice_date),
  );
  const vehicleDeliveryDate = getDeliveryDate(loan);
  const rcDate = toDateOrNull(
    firstFilled(loan?.rc_received_date, loan?.rc_received_at),
  );

  return {
    customerProfile: customerProfileDate?.toISOString() || null,
    prefileCompletion: prefileCompletionDate?.toISOString() || null,
    loginToBank: loginToBankDate?.toISOString() || null,
    approval: approvalDate?.toISOString() || null,
    disbursement: disbursementDate?.toISOString() || null,
    postfileCompletion: postfileCompletionDate?.toISOString() || null,
    documentsAdded: documentsAddedDate?.toISOString() || null,
    insurance: insuranceDate?.toISOString() || null,
    invoice: invoiceDate?.toISOString() || null,
    vehicleDelivery: vehicleDeliveryDate?.toISOString() || null,
    rc: rcDate?.toISOString() || null,
  };
};

const buildTimeline = (loan) => {
  const raw = buildRawTimeline(loan);
  return STAGES.map((s) => ({
    ...s,
    date: toDateOrNull(raw[s.key]),
  }));
};

const findCurrentStageIndex = (loan) => {
  const steps = buildTimeline(loan);
  let lastDone = -1;
  steps.forEach((s, i) => {
    if (s.date) lastDone = i;
  });
  if (lastDone !== -1) return lastDone;

  const byStage = findStageIndexByCurrentStage(loan);
  return byStage !== -1 ? byStage : 0;
};

const getMiniWindow = (loan) => {
  const steps = buildTimeline(loan);
  const current = findCurrentStageIndex(loan);
  const prev = current - 1 >= 0 ? steps[current - 1] : null;
  const curr = steps[current] || null;
  const next = current + 1 < steps.length ? steps[current + 1] : null;

  return {
    steps: [prev, curr, next],
    currentKey: curr?.key,
  };
};

const pickPrimaryBank = (loan) => {
  const banks = loan?.approval_banksData || [];
  return (
    banks.find((b) => b.status === "Disbursed") ||
    banks.find((b) => b.status === "Approved") ||
    banks[0] ||
    null
  );
};

const LoansDataGrid = ({
  loans,
  totalCount,
  currentPage = 1,
  pageSize = 75,
  onPageChange,
  selectedLoans,
  onSelectLoan,
  onSelectAll,
  onLoanClick,
  onBulkAction,
  onDeleteLoan,
  userRole,
  loading,
  onUpdateStatus,
  onOpenRepaymentIntelligence,
  onShowOtherBanks,
  onRefreshLoans,
  onNotesClick,
  onOpenLoanDeliverySection,
}) => {
  const [timelineLoan, setTimelineLoan] = useState(null);
  const [pendencyLoan, setPendencyLoan] = useState(null);
  const [pendencyDraft, setPendencyDraft] = useState({
    loanNumber: "",
    regNumber: "",
  });
  const [savingPendency, setSavingPendency] = useState(false);
  const [documentsLoan, setDocumentsLoan] = useState(null);
  const [closureLoan, setClosureLoan] = useState(null);

  const formatLoanId = (id) => {
    if (!id) return "Loan number not set";
    return String(id);
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return "Cash Sale";
    return formatINR(amount);
  };

  const parseAmount = (value) => {
    if (value == null || value === "") return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    const cleaned = String(value).replace(/[^\d.-]/g, "");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const sortedLoans = loans || [];

  const getPendingSteps = (loan) => {
    const rows = buildPendencyRows(loan);
    return rows.filter((row) => !row.isReceived).map((row) => row.label);
  };

  const loansWithPendency = useMemo(
    () =>
      (sortedLoans || []).map((loan) => {
        const pendencyGate = getPendencyGateInfo(loan);
        const pendingSteps = pendencyGate.active ? getPendingSteps(loan) : [];
        return {
          ...loan,
          pendencyCount: pendingSteps.length,
          pendingSteps,
          pendencyActive: pendencyGate.active,
          pendencyGateMessage: pendencyGate.message,
        };
      }),
    [sortedLoans],
  );

  const openPendencyModal = (loan) => {
    setPendencyLoan(loan);
    setPendencyDraft({
      loanNumber: getActualLoanNumber(loan),
      regNumber:
        firstMeaningful(
          loan?.rc_redg_no,
          loan?.vehicleRegNo,
          loan?.registrationNumber,
          loan?.vehicleNumber,
          "",
        ) || "",
    });
  };

  const handleSavePendency = async () => {
    if (!pendencyLoan) return;
    const targetId =
      pendencyLoan?._id || pendencyLoan?.loanId || pendencyLoan?.loan_number;
    if (!targetId) {
      message.error("Loan identifier missing.");
      return;
    }
    const nextLoanNo = String(pendencyDraft.loanNumber || "").trim();
    const nextRegNo = String(pendencyDraft.regNumber || "").trim();
    const currLoanNo = String(getActualLoanNumber(pendencyLoan) || "").trim();
    const currRegNo = String(
      firstMeaningful(
        pendencyLoan?.rc_redg_no,
        pendencyLoan?.vehicleRegNo,
        pendencyLoan?.registrationNumber,
        pendencyLoan?.vehicleNumber,
        "",
      ) || "",
    ).trim();

    const patch = {};
    if (nextLoanNo !== currLoanNo) patch.loan_number = nextLoanNo;
    if (nextRegNo !== currRegNo) {
      patch.rc_redg_no = nextRegNo;
      patch.vehicleRegNo = nextRegNo;
      patch.registrationNumber = nextRegNo;
    }

    if (!Object.keys(patch).length) {
      message.info("No changes to save.");
      return;
    }

    try {
      setSavingPendency(true);
      await homeLoansApi.update(targetId, patch);
      setPendencyLoan((prev) => (prev ? { ...prev, ...patch } : prev));
      message.success("Pendency details updated.");
      onRefreshLoans?.();
    } catch (error) {
      message.error(error?.message || "Unable to save pendency details.");
    } finally {
      setSavingPendency(false);
    }
  };

  const allChecked =
    selectedLoans?.length === sortedLoans?.length && sortedLoans?.length > 0;
  const someChecked =
    selectedLoans?.length > 0 && selectedLoans?.length < sortedLoans?.length;
  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize));
  const pendencyRows = useMemo(
    () => (pendencyLoan ? buildPendencyRows(pendencyLoan) : []),
    [pendencyLoan],
  );
  const pendingRowCount = useMemo(
    () => pendencyRows.filter((row) => !row.isReceived).length,
    [pendencyRows],
  );
  const pendencyIsNewCar = useMemo(
    () => (pendencyLoan ? isNewCarLoan(pendencyLoan) : false),
    [pendencyLoan],
  );
  const pendencyIsCashCase = useMemo(
    () => (pendencyLoan ? isCashCaseLoan(pendencyLoan) : false),
    [pendencyLoan],
  );
  const pendencyGateInfo = useMemo(
    () =>
      pendencyLoan
        ? getPendencyGateInfo(pendencyLoan)
        : { active: true, message: "" },
    [pendencyLoan],
  );

  return (
    <div className="h-full flex flex-col overflow-hidden bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-3 py-2">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked}
            onChange={(e) => onSelectAll(e?.target?.checked)}
          />
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
              Visual Loan Board
            </p>
            <p className="text-sm font-semibold text-foreground">
              {totalCount || sortedLoans?.length || 0} case(s) in current result
            </p>
          </div>
        </div>

        {selectedLoans?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="FileDown"
              onClick={() => onBulkAction("export")}
            >
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Send"
              onClick={() => onBulkAction("dispatch")}
            >
              Dispatch
            </Button>
            {userRole === "admin" && (
              <Button
                variant="default"
                size="sm"
                iconName="CheckCircle2"
                onClick={() => onBulkAction("approve")}
              >
                Approve
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-3 bg-background">
        {loading && (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Loading loans...
          </div>
        )}

        {!loading && sortedLoans?.length === 0 && (
          <div className="rounded-xl border border-dashed border-border p-8 text-center">
            <Icon
              name="FileX2"
              size={44}
              className="mx-auto text-muted-foreground"
            />
            <p className="mt-3 text-base font-semibold text-foreground">
              No loans found
            </p>
            <p className="text-sm text-muted-foreground">
              Try adjusting filters or create a new case.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-2.5">
          {!loading &&
            loansWithPendency?.map((loanWithPendency, index) => {
              const loan = loanWithPendency;
              const pendencyCount = loanWithPendency?.pendencyCount || 0;
              const pendingSteps = loanWithPendency?.pendingSteps || [];
              const pendencyActive = loanWithPendency?.pendencyActive !== false;
              const pendencyGateMessage = loanWithPendency?.pendencyGateMessage;

              const loanKey =
                loan?._id ||
                loan?.loan_number ||
                loan?.loanId ||
                `loan-${index}`;
              const statusText = loan?.status || "New";

              const fullCarName =
                [loan?.propertyType, loan?.typeOfLoan].filter(Boolean).join(" • ");
              const carTitle = fullCarName || "Property not selected";
              const variant = loan?.vehicleVariant || "Variant not set";
              const regNo =
                loan?.rc_redg_no ||
                loan?.vehicleRegNo ||
                loan?.registrationNumber ||
                loan?.vehicleNumber ||
                "Reg no not set";
              const regCity =
                loan?.postfile_regd_city || loan?.registrationCity || "-";

              const primary = pickPrimaryBank(loan);
              const banks = loan?.approval_banksData || [];
              const otherBanks = banks.filter(
                (b) => primary && b?.id !== primary.id,
              );

              const primaryBankName =
                primary?.bankName || loan?.approval_bankName || "Bank not set";
              const rawPrimaryLoanAmount =
                parseAmount(primary?.loanAmount) ||
                loan?.approval_loanAmountDisbursed ||
                loan?.approval_loanAmountApproved ||
                loan?.financeExpectation ||
                0;
              const loanTypeText = String(
                loan?.typeOfLoan ||
                  loan?.loanType ||
                  loan?.caseType ||
                  loan?.loan_type ||
                  "",
              ).toLowerCase();
              const caseTypeLabel =
                loan?.typeOfLoan ||
                loan?.loanType ||
                loan?.caseType ||
                loan?.loan_type ||
                "Case";
              const primaryInterest =
                typeof primary?.interestRate === "number"
                  ? primary.interestRate
                  : loan?.approval_roi;
              const primaryTenureMonths =
                primary?.tenure ||
                loan?.approval_tenureMonths ||
                loan?.loanTenureMonths ||
                null;
              const primaryEmiAmount =
                parseAmount(primary?.emiAmount) ||
                parseAmount(primary?.emi) ||
                parseAmount(loan?.postfile_emiAmount) ||
                parseAmount(loan?.emiAmount) ||
                0;
              const isCashCar = (() => {
                const isFinancedRaw =
                  loan?.isFinanced ?? loan?.isFinanceRequired;
                const isFinancedText = String(isFinancedRaw ?? "")
                  .trim()
                  .toLowerCase();
                if (isFinancedText === "no" || isFinancedText === "false")
                  return true;
                if (isFinancedText === "yes" || isFinancedText === "true")
                  return false;
                if (
                  loanTypeText === "cash" ||
                  loanTypeText.includes("cash car") ||
                  loanTypeText.includes("cash sale")
                ) {
                  return true;
                }
                if (
                  loanTypeText.includes("cash-in") ||
                  loanTypeText.includes("cash in")
                ) {
                  return false;
                }
                if (
                  loan?.isFinanced === false ||
                  loan?.isFinanceRequired === false
                )
                  return true;
                const financeExpectation = parseAmount(
                  loan?.financeExpectation,
                );
                const bankLoanAmount = parseAmount(primary?.loanAmount);
                const hasBankName = !!(
                  primary?.bankName || loan?.approval_bankName
                );
                const noFinanceMetrics =
                  bankLoanAmount === 0 &&
                  primaryEmiAmount === 0 &&
                  !primaryInterest &&
                  !primaryTenureMonths;
                return (
                  (financeExpectation === 0 && !hasBankName) || noFinanceMetrics
                );
              })();
              const primaryLoanAmount = isCashCar ? 0 : rawPrimaryLoanAmount;
              const disbursementDate =
                loan?.disbursement_date ||
                loan?.approval_disbursedDate ||
                loan?.disbursedDate ||
                loan?.disbursementDate ||
                primary?.disbursedDate ||
                null;
              const storedPrincipalOutstanding =
                parseAmount(loan?.postfile_currentOutstanding) ||
                parseAmount(loan?.postfile_current_outstanding) ||
                parseAmount(loan?.currentOutstanding) ||
                parseAmount(loan?.livePrincipalOutstanding) ||
                parseAmount(loan?.live_principal_outstanding) ||
                parseAmount(loan?.principalOutstanding) ||
                parseAmount(loan?.principal_outstanding) ||
                parseAmount(loan?.outstandingPrincipal) ||
                parseAmount(loan?.outstandingBalance) ||
                parseAmount(loan?.postfile_principalOutstanding) ||
                parseAmount(loan?.postfile_livePrincipalOutstanding) ||
                parseAmount(loan?.postFile?.currentOutstanding) ||
                0;
              const liveOutstandingFallback =
                !storedPrincipalOutstanding &&
                !isCashCar &&
                primaryLoanAmount &&
                primaryInterest &&
                primaryTenureMonths
                  ? calculateLivePrincipalOutstanding(
                      primaryLoanAmount,
                      primaryInterest,
                      primaryTenureMonths,
                      loan?.postfile_firstEmiDate || disbursementDate,
                    )?.outstanding || 0
                  : 0;
              const principalOutstanding =
                storedPrincipalOutstanding ||
                parseAmount(liveOutstandingFallback);
              const isLoanClosed =
                !!loan?.closureDate ||
                !!loan?.closedDate ||
                String(loan?.loanStatus || loan?.status || "")
                  .toLowerCase()
                  .includes("closed");
              const customerAddress =
                loan?.residenceAddress ||
                loan?.permanentAddress ||
                loan?.address ||
                "";
              const customerCity = loan?.city || loan?.permanentCity || "";
              const normalizedCaseType = String(
                loan?.typeOfLoan ||
                  loan?.loanType ||
                  loan?.caseType ||
                  loan?.loan_type ||
                  "",
              )
                .trim()
                .toLowerCase();
              const isNewCarCase = normalizedCaseType === "new car";
              const showroomFieldLabel = isNewCarCase
                ? "Showroom"
                : "Loan Payment Favouring";
              // Show post-file/delivery showroom or loan payment-favouring source.
              const showroomName = firstMeaningful(
                loan?.showroomDealerName,
                loan?.delivery_dealerName,
                loan?.dealerName,
                loan?.showroomName,
                loan?.showroom,
                loan?.showroom_name,
              );
              const disbursementLabel = disbursementDate
                ? new Date(disbursementDate).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "";
              const cashDeliveryDate = getDeliveryDate(loan);
              const cashDeliveryLabel = cashDeliveryDate
                ? cashDeliveryDate.toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "";
              const firstEmiDateForClosure =
                loan?.postfile_firstEmiDate ||
                loan?.postfile_first_emi_date ||
                primary?.firstEmiDate ||
                loan?.firstEmiDate ||
                disbursementDate ||
                null;
              const statusTextLower = String(statusText || "").toLowerCase();
              const hasDisbursedBank = (banks || []).some((bank) =>
                String(bank?.status || "")
                  .toLowerCase()
                  .includes("disburs"),
              );
              const isDisbursedCase =
                Boolean(toDateOrNull(disbursementDate)) ||
                hasDisbursedBank ||
                statusTextLower.includes("disburs");
              const isClosedByStatus = isLoanClosed;
              const activeStatusLabel =
                !isCashCar && isDisbursedCase && !isClosedByStatus
                  ? "Active"
                  : null;
              const sourceText = firstMeaningful(
                loan?.source,
                loan?.recordSource,
                "",
              );
              const sourceNameText = firstMeaningful(
                loan?.sourceName,
                loan?.dealerName,
                loan?.paymentFavouring,
                loan?.loanPaymentFavouring,
                loan?.loan_payment_favouring,
                "",
              );
              const loanBookedInMode =
                loan?.approval_loanBookedIn ||
                primary?.loanBookedIn ||
                "Direct Code";
              const brokerOrCorporateDsaName =
                loanBookedInMode === "Indirect Code"
                  ? (
                      loan?.approval_brokerName ||
                      primary?.brokerName ||
                      ""
                    ).trim()
                  : "";
              const referenceName = hasDisplayValue(loan?.referenceName)
                ? String(loan.referenceName).trim()
                : "";
              const hasFinanceMeta =
                primaryInterest != null || !!primaryTenureMonths;

              const miniWindowData = getMiniWindow(loan);
              const currentStageIndex = findCurrentStageIndex(loan);
              const missingEmi = !isCashCar && !primaryEmiAmount;
              const missingRegNo = regNo === "Reg no not set";
              const prefileCompletion = computePrefileCompletion(loan, {
                isCashCase: isCashCar,
              });
              const showCpvIncomplete =
                prefileCompletion.totalSections > 0 &&
                prefileCompletion.isIncomplete;
              const showCpvComplete =
                prefileCompletion.totalSections > 0 &&
                !prefileCompletion.isIncomplete;
              const cpvTooltip = `CPV ${prefileCompletion.percentage}% (${prefileCompletion.completedSections}/${prefileCompletion.totalSections} sections)${
                prefileCompletion.pendingSections?.length
                  ? ` | Pending: ${prefileCompletion.pendingSections.join(", ")}`
                  : ""
              }`;

              return (
                <article
                  key={loanKey}
                  className="group rounded-xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <div className="border-b border-border px-3 py-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={selectedLoans?.includes(loan?.loanId)}
                          onChange={(e) =>
                            onSelectLoan(loan?.loanId, e?.target?.checked)
                          }
                        />
                        <div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                            <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                              {isCashCar ? "Cash Sale" : "Loan"}
                            </span>
                            <span className="rounded-full border border-indigo-300 bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-300">
                              {caseTypeLabel}
                            </span>
                            <span className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-bold text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                              {formatLoanId(loan?.loan_number || loan?.loanId)}
                            </span>
                            {!isCashCar && hasDisplayValue(primaryBankName) && (
                              <span className="rounded-full border border-fuchsia-300 bg-fuchsia-50 px-2.5 py-1 text-[11px] font-bold text-fuchsia-700 dark:border-fuchsia-800 dark:bg-fuchsia-950/40 dark:text-fuchsia-300">
                                {primaryBankName}
                              </span>
                            )}
                            {!isCashCar &&
                              hasDisplayValue(disbursementLabel) && (
                                <span className="rounded-full border border-cyan-300 bg-cyan-50 px-2.5 py-1 text-[11px] font-semibold text-cyan-700 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-300">
                                  Disb: {disbursementLabel}
                                </span>
                              )}
                            {isCashCar && hasDisplayValue(cashDeliveryLabel) && (
                              <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                                Delivery: {cashDeliveryLabel}
                              </span>
                            )}
                            {!isCashCar &&
                              isDisbursedCase &&
                              !isClosedByStatus &&
                              !!principalOutstanding && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setClosureLoan({
                                      loanId:
                                        loan?.loan_number ||
                                        loan?.loanId ||
                                        loan?._id ||
                                        null,
                                      customerName:
                                        loan?.customerName || "Loan",
                                      principalOutstanding,
                                      disbursedAmount: primaryLoanAmount,
                                      interestRate: primaryInterest,
                                      tenureMonths: primaryTenureMonths,
                                      firstEmiDate: firstEmiDateForClosure,
                                    });
                                  }}
                                  className="rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-900/55"
                                  title="Open Approx Closure"
                                >
                                  Live POS: {formatINR(principalOutstanding)}
                                </button>
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-1.5">
                        {activeStatusLabel ? (
                          <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
                            {activeStatusLabel}
                          </span>
                        ) : null}
                        <span className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          {loan?.currentStage || "profile"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 space-y-2">
                    <section className="grid grid-cols-1 gap-2 lg:grid-cols-5">
                      <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-2 dark:border-slate-800 dark:bg-slate-950/60">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                          Customer
                        </p>
                        <p className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-100">
                          {hasDisplayValue(loan?.customerName)
                            ? loan.customerName
                            : "Customer"}
                        </p>
                        {hasDisplayValue(loan?.primaryMobile) && (
                          <p className="text-[11px] text-slate-600 dark:text-slate-300">
                            {loan.primaryMobile}
                          </p>
                        )}
                        {hasDisplayValue(customerAddress) && (
                          <p className="text-[10px] text-slate-500 dark:text-slate-400">
                            {customerAddress}
                          </p>
                        )}
                        {hasDisplayValue(customerCity) && (
                          <p className="text-[10px] text-slate-500 dark:text-slate-400">
                            {customerCity}
                          </p>
                        )}
                      </div>

                      <div className="rounded-lg border border-sky-200 bg-sky-50/80 p-2 dark:border-sky-900/70 dark:bg-sky-950/40">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-700 dark:text-sky-300">
                          Vehicle
                        </p>
                        <p className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                          {carTitle}
                        </p>
                        <p className="text-[10px] text-slate-600 dark:text-slate-300 truncate">
                          {variant}
                        </p>
                        <p className="text-[10px] font-semibold text-sky-700 dark:text-sky-300 truncate">
                          Reg: {regNo}
                        </p>
                      </div>

                      <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50/80 p-2 dark:border-fuchsia-900/70 dark:bg-fuchsia-950/40">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-fuchsia-700 dark:text-fuchsia-300">
                          Finance
                        </p>
                        {isCashCar ? (
                          <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">
                            Cash car
                          </p>
                        ) : (
                          <p className="text-[11px] text-slate-700 dark:text-slate-200">
                            Loan:{" "}
                            <span className="font-semibold">
                              {formatCurrency(primaryLoanAmount)}
                            </span>
                          </p>
                        )}
                        {!isCashCar && !!primaryEmiAmount && (
                          <p className="text-[11px] text-slate-700 dark:text-slate-200">
                            EMI:{" "}
                            <span className="font-semibold">
                              {formatINR(primaryEmiAmount)}
                            </span>
                          </p>
                        )}
                        {!isCashCar && hasFinanceMeta && (
                          <p className="text-[10px] text-slate-600 dark:text-slate-300 truncate">
                            {primaryInterest != null
                              ? `ROI ${primaryInterest}%`
                              : ""}
                            {primaryInterest != null && primaryTenureMonths
                              ? " · "
                              : ""}
                            {primaryTenureMonths
                              ? `${primaryTenureMonths}m`
                              : ""}
                          </p>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {activeStatusLabel ? (
                            <span className="inline-flex w-fit rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
                              {activeStatusLabel}
                            </span>
                          ) : null}
                          {otherBanks.length > 0 && (
                            <button
                              type="button"
                              className="rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300"
                              onClick={() =>
                                onShowOtherBanks &&
                                onShowOtherBanks(loan, otherBanks)
                              }
                            >
                              +{otherBanks.length} banks
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-2 dark:border-slate-800 dark:bg-slate-950/60">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                          Sourcing
                        </p>
                        {hasDisplayValue(sourceText) && (
                          <p className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                            {sourceText}
                          </p>
                        )}
                        {hasDisplayValue(sourceNameText) && (
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                            {sourceNameText}
                          </p>
                        )}
                        {hasDisplayValue(brokerOrCorporateDsaName) && (
                          <p className="text-[10px] font-bold text-slate-700 dark:text-slate-200 truncate">
                            Broker/Corporate DSA: {brokerOrCorporateDsaName}
                          </p>
                        )}
                        {hasDisplayValue(showroomName) && (
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                            {showroomFieldLabel}: {showroomName || "-"}
                          </p>
                        )}
                        {hasDisplayValue(referenceName) && (
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                            Ref: {referenceName}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        className="w-full rounded-lg border border-slate-200 bg-slate-50/70 p-2 text-left transition-colors hover:border-slate-300 hover:bg-slate-100/80 dark:border-slate-800 dark:bg-slate-950/60 dark:hover:border-slate-700 dark:hover:bg-slate-900/80"
                        onClick={() => setTimelineLoan(loan)}
                        title="View full timeline"
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                          Timeline
                        </p>
                        <div className="relative mt-1.5 min-h-[56px]">
                          <div className="absolute bottom-1 left-[7px] top-1 w-px bg-slate-300 dark:bg-slate-700" />
                          {miniWindowData.steps.map((step, idx) => {
                            if (!step) {
                              return (
                                <div
                                  key={`empty-${idx}`}
                                  className="grid min-h-[16px] items-center gap-2"
                                  style={{ gridTemplateColumns: "14px 1fr" }}
                                >
                                  <span className="relative z-10 mx-auto block h-2.5 w-2.5 rounded-full border border-slate-300 bg-slate-200 dark:border-slate-700 dark:bg-slate-800" />
                                  <span className="text-[10px] text-slate-500 dark:text-slate-400">
                                    —
                                  </span>
                                </div>
                              );
                            }
                            const isCurrent =
                              step.key === miniWindowData.currentKey;
                            return (
                              <div
                                key={step.key || `step-${idx}`}
                                className={`grid min-h-[16px] items-center gap-2 ${isCurrent ? "" : "opacity-80"}`}
                                style={{ gridTemplateColumns: "14px 1fr" }}
                              >
                                <span
                                  className={`relative z-10 rounded-full border ${
                                    isCurrent
                                      ? "h-2.5 w-2.5 border-amber-500 bg-amber-500"
                                      : step.date
                                        ? "h-2.5 w-2.5 border-emerald-500 bg-emerald-500"
                                        : "h-2.5 w-2.5 border-slate-300 bg-slate-200 dark:border-slate-700 dark:bg-slate-800"
                                  } mx-auto block`}
                                />
                                <span
                                  className={`max-w-[130px] truncate text-[10px] ${isCurrent ? "font-semibold text-slate-900 dark:text-slate-100" : "text-slate-600 dark:text-slate-300"}`}
                                >
                                  {step.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        <p className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                          Stage {currentStageIndex + 1}/{STAGES.length}
                        </p>
                      </button>
                    </section>

                    <section className="flex flex-wrap items-center justify-between gap-1.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {missingEmi && (
                          <span className="rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
                            EMI missing
                          </span>
                        )}
                        {missingRegNo && (
                          <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                            Reg no missing
                          </span>
                        )}
                        <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          Reg City: {regCity}
                        </span>
                        {showCpvIncomplete && (
                          <Tooltip
                            title={cpvTooltip}
                            placement="top"
                          >
                            <span className="rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
                              CPV incomplete
                            </span>
                          </Tooltip>
                        )}
                        {showCpvComplete && (
                          <Tooltip
                            title={cpvTooltip}
                            placement="top"
                          >
                            <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
                              CPV complete
                            </span>
                          </Tooltip>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-1">
                        <Tooltip title="View loan details" placement="top">
                          <button
                            type="button"
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-sky-100 text-sky-700 border border-sky-200 hover:bg-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800"
                            onClick={() => onLoanClick(loan, "view")}
                          >
                            <Icon name="Eye" size={12} />
                          </button>
                        </Tooltip>
                        <Tooltip title="Open documents" placement="top">
                          <button
                            type="button"
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
                            onClick={() => setDocumentsLoan(loan)}
                          >
                            <Icon name="FolderOpen" size={12} />
                          </button>
                        </Tooltip>
                        <Tooltip title="Edit case" placement="top">
                          <button
                            type="button"
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
                            onClick={() => onLoanClick(loan, "edit")}
                          >
                            <Icon name="Edit" size={12} />
                          </button>
                        </Tooltip>
                        <Tooltip title="Repayment intelligence" placement="top">
                          <button
                            type="button"
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
                            onClick={() => onOpenRepaymentIntelligence?.(loan)}
                          >
                            <Icon name="TrendingUp" size={12} />
                          </button>
                        </Tooltip>
                        <Tooltip title="Update status" placement="top">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-300"
                            onClick={() => onUpdateStatus?.(loan)}
                          >
                            <Icon name="Flag" size={12} /> Update
                          </button>
                        </Tooltip>
                        <Tooltip
                          title={
                            !pendencyActive
                              ? pendencyGateMessage
                              : pendencyCount > 0
                              ? `Pending: ${pendingSteps.join(", ")}`
                              : "No pendency"
                          }
                          placement="top"
                        >
                          <button
                            type="button"
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                              !pendencyActive
                                ? "border-slate-300 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                : pendencyCount > 0
                                ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300"
                                : "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                            }`}
                            onClick={() => openPendencyModal(loan)}
                          >
                            <Icon name="AlertTriangle" size={12} /> Pendency{" "}
                            {pendencyCount > 0 ? `(${pendencyCount})` : ""}
                          </button>
                        </Tooltip>
                        <Tooltip title="Internal notes" placement="top">
                          <button
                            type="button"
                            className={`w-7 h-7 flex items-center justify-center rounded-full border ${
                              loan.loan_notes
                                ? "bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800"
                                : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
                            }`}
                            onClick={() => onNotesClick?.(loan)}
                          >
                            <Icon name="StickyNote" size={12} />
                          </button>
                        </Tooltip>
                        {userRole === "admin" && (
                          <Tooltip title="Delete case" placement="top">
                            <button
                              type="button"
                              className="w-7 h-7 flex items-center justify-center rounded-full bg-rose-100 text-rose-700 border border-rose-200 hover:bg-rose-200 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-800"
                              onClick={() => onDeleteLoan?.(loan)}
                            >
                              <Icon name="Trash2" size={12} />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </section>
                  </div>
                </article>
              );
            })}
        </div>

        {!loading && (totalCount || 0) > 0 && (
          <div className="mt-3 flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
            <div>
              Showing{" "}
              <span className="font-semibold text-foreground">
                {Math.min((currentPage - 1) * pageSize + 1, totalCount || 0)}
              </span>{" "}
              -{" "}
              <span className="font-semibold text-foreground">
                {Math.min(currentPage * pageSize, totalCount || 0)}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-foreground">
                {totalCount || 0}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={currentPage <= 1}
                onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
              >
                Prev
              </Button>
              <span className="px-2 text-foreground font-medium">
                {currentPage}/{totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                disabled={currentPage >= totalPages}
                onClick={() =>
                  onPageChange?.(Math.min(totalPages, currentPage + 1))
                }
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <LoanDocumentsModal
        loan={documentsLoan}
        open={!!documentsLoan}
        onClose={() => setDocumentsLoan(null)}
        onUploadComplete={() => {
          onRefreshLoans?.();
        }}
      />
      <ApproxClosureModal
        open={!!closureLoan}
        data={closureLoan}
        onClose={() => setClosureLoan(null)}
      />

      {pendencyLoan && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm"
          onClick={() => setPendencyLoan(null)}
        >
          <div
            className="relative w-full max-w-4xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-border bg-gradient-to-r from-rose-50 via-amber-50 to-sky-50 px-6 py-4 dark:from-rose-950/20 dark:via-amber-950/20 dark:to-sky-950/20">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-rose-600 dark:text-rose-300">
                    Pendency Control
                  </p>
                  <h3 className="truncate text-lg font-black text-foreground">
                    {pendencyLoan.customerName || "Customer"} ·{" "}
                    {formatLoanId(
                      pendencyLoan.loanId || pendencyLoan.loan_number,
                    )}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {pendencyIsNewCar
                      ? pendencyIsCashCase
                        ? "New Car (Cash): Invoice, RC, Regd No."
                        : "New Car: Invoice, RC, Loan No., Regd No."
                      : pendencyIsCashCase
                      ? "Other Cases (Cash): RC only."
                      : "Other Cases: RC and Loan No."}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${
                      pendingRowCount > 0
                        ? "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300"
                        : "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
                    }`}
                  >
                    {pendingRowCount > 0
                      ? `${pendingRowCount} pending`
                      : "All complete"}
                  </span>
                  <button
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={() => setPendencyLoan(null)}
                    title="Close"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-4 px-6 py-5">
              {!pendencyGateInfo.active ? (
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
                  {pendencyGateInfo.message}
                </div>
              ) : null}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {pendencyRows.map((row) => {
                  const isNegative = typeof row.ageingDays === "number" && row.ageingDays < 0;
                  const isPending = !row.isReceived;
                  const isAgeRed = isNegative;
                  const ageingText =
                    typeof row.ageingDays === "number"
                      ? `${row.ageingDays} day${Math.abs(row.ageingDays) === 1 ? "" : "s"}`
                      : "NA";
                  return (
                    <div
                      key={row.key}
                      className={`rounded-xl border p-3 ${
                        isPending
                          ? "border-amber-300 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/25"
                          : "border-emerald-300 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-foreground">{row.label}</p>
                          <p className="text-[11px] text-muted-foreground">
                            Base: {formatDateShort(row.baselineDate)}
                          </p>
                        </div>
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                            isPending
                              ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300"
                              : "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
                          }`}
                        >
                          {isPending ? "Pending" : "Received"}
                        </span>
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-lg border border-border/70 bg-background/90 px-2 py-1.5">
                          <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                            Received Date
                          </p>
                          <p className="font-semibold text-foreground">
                            {formatDateShort(
                              row.completedDate || (row.isReceived ? new Date() : null),
                            )}
                          </p>
                        </div>
                        <div className="rounded-lg border border-border/70 bg-background/90 px-2 py-1.5">
                          <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                            Ageing
                          </p>
                          <p
                            className={`font-bold ${
                              isAgeRed
                                ? "text-rose-600 dark:text-rose-400"
                                : "text-emerald-700 dark:text-emerald-300"
                            }`}
                          >
                            {ageingText}
                          </p>
                        </div>
                      </div>

                      {row.editable && row.key === "loanNo" ? (
                        <div className="mt-2">
                          <label className="text-[11px] text-muted-foreground">
                            Update Loan No.
                          </label>
                          <input
                            className="mt-1 w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground outline-none transition-colors focus:border-primary/60"
                            value={pendencyDraft.loanNumber}
                            onChange={(e) =>
                              setPendencyDraft((prev) => ({
                                ...prev,
                                loanNumber: e.target.value,
                              }))
                            }
                            placeholder="Enter loan number"
                          />
                        </div>
                      ) : null}

                      {row.editable && row.key === "regNo" ? (
                        <div className="mt-2">
                          <label className="text-[11px] text-muted-foreground">
                            Update Regd No.
                          </label>
                          <input
                            className="mt-1 w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground outline-none transition-colors focus:border-primary/60"
                            value={pendencyDraft.regNumber}
                            onChange={(e) =>
                              setPendencyDraft((prev) => ({
                                ...prev,
                                regNumber: e.target.value.toUpperCase(),
                              }))
                            }
                            placeholder="Enter registration number"
                          />
                        </div>
                      ) : null}

                      {row.actionType && isPending ? (
                        <div className="mt-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-700 transition-colors hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300"
                            onClick={() => {
                              onOpenLoanDeliverySection?.(pendencyLoan, row.actionType);
                              setPendencyLoan(null);
                            }}
                          >
                            <Icon name="Upload" size={12} />
                            {row.actionType === "delivery-rc"
                              ? "Upload RC"
                              : "Upload Invoice"}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPendencyLoan(null)}
                  disabled={savingPendency}
                >
                  Close
                </Button>
                <Button size="sm" onClick={handleSavePendency} disabled={savingPendency}>
                  {savingPendency ? "Saving..." : "Save Updates"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {timelineLoan && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setTimelineLoan(null)}
        >
          <div
            className="flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border bg-muted/30 px-5 py-4">
              <div className="flex flex-col gap-0.5">
                <span className="text-base font-bold text-foreground tracking-tight">
                  Loan Timeline
                </span>
                <span className="text-xs text-muted-foreground">
                  {timelineLoan.customerName || "Customer"} ·{" "}
                  {formatLoanId(
                    timelineLoan.loanId || timelineLoan.loan_number,
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  Stage {findCurrentStageIndex(timelineLoan) + 1}/
                  {STAGES.length}
                </span>
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => setTimelineLoan(null)}
                  title="Close"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <div className="relative rounded-xl border border-border bg-background px-3 py-3">
                <div className="space-y-0">
                  {(() => {
                    const currentIndex = findCurrentStageIndex(timelineLoan);
                    const currentStageKey = STAGES[currentIndex]?.key;
                    const timelineSteps = buildTimeline(timelineLoan);
                    return timelineSteps.map((step, idx) => {
                      const isCurrent = currentStageKey === step.key;
                      const isDone = step.date != null;
                      const isFirst = idx === 0;
                      const isLast = idx === timelineSteps.length - 1;

                      return (
                        <div
                          key={step.key}
                          className="grid items-start gap-3 py-1"
                          style={{ gridTemplateColumns: "24px 1fr" }}
                        >
                          <div className="relative z-10 h-9 w-6">
                            {!isFirst && (
                              <span className="absolute left-1/2 top-0 h-1/2 w-px -translate-x-1/2 bg-border" />
                            )}
                            {!isLast && (
                              <span className="absolute left-1/2 top-1/2 h-1/2 w-px -translate-x-1/2 bg-border" />
                            )}
                            <span
                              className={`absolute left-1/2 top-1/2 block h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border ${
                                isCurrent
                                  ? "border-amber-500 bg-amber-500"
                                  : isDone
                                    ? "border-emerald-500 bg-emerald-500"
                                    : "border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950"
                              }`}
                            />
                          </div>
                          <div
                            className={`rounded-lg border px-3 py-2 ${
                              isCurrent
                                ? "border-amber-300 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/25"
                                : isDone
                                  ? "border-emerald-300 bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/25"
                                  : "border-border bg-card"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <p
                                className={`truncate text-sm ${isCurrent ? "font-semibold text-foreground" : "text-foreground/90"}`}
                              >
                                {step.label}
                              </p>
                              <span
                                className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                                  isCurrent
                                    ? "border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300"
                                    : isDone
                                      ? "border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300"
                                      : "border-slate-300 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                                }`}
                              >
                                {isCurrent
                                  ? "Current"
                                  : isDone
                                    ? "Done"
                                    : "Pending"}
                              </span>
                            </div>
                            <p className="mt-1 text-[11px] text-muted-foreground">
                              {step.date
                                ? new Date(step.date).toLocaleDateString(
                                    "en-IN",
                                  )
                                : "Date not available"}
                            </p>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoansDataGrid;
